// @section 1 Atlas Booker — manage /book/<slug> schedules from a browser.
// Lists existing schedules with copyable URLs + delete buttons. A compact form
// at the top creates new schedules from the zach-hintze template (4 required
// inputs: slug + duration + intake; everything else inherits).
//
// Data: GET/POST/DELETE /api/hq/booking-schedules (HqBookingSchedulesEndpoints
// on the OptiqOS API, AnyAuth).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookingScheduleListRow,
  CreateBookingScheduleBody,
  createBookingSchedule,
  deleteBookingSchedule,
  listBookingSchedules,
} from '../api';

// @subsection 1.1 Intake field catalog (mirrors the server's known IDs)
type IntakeFieldKey = 'fullName' | 'email' | 'phone' | 'companyName' | 'truckCount' | 'currentSoftware';

const INTAKE_CATALOG: { key: IntakeFieldKey; label: string; type: string; placeholder: string }[] = [
  { key: 'fullName',        label: 'Your Name',                 type: 'text',   placeholder: 'Your name' },
  { key: 'email',           label: 'Email',                     type: 'email',  placeholder: 'you@example.com' },
  { key: 'phone',           label: 'Phone',                     type: 'tel',    placeholder: '(555) 123-4567' },
  { key: 'companyName',     label: 'Company Name',              type: 'text',   placeholder: 'Acme Inc.' },
  { key: 'truckCount',      label: 'Number of Trucks / Techs',  type: 'number', placeholder: 'e.g. 5' },
  { key: 'currentSoftware', label: 'Current Software',          type: 'text',   placeholder: 'e.g. ServiceTitan' },
];

// @section 2 Component
export default function Booker() {
  const [rows, setRows] = useState<BookingScheduleListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listBookingSchedules(false);
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(
    () => rows.filter((r) => showInactive || r.isActive),
    [rows, showInactive],
  );

  // @subsection 2.1 Create form state
  const [slug, setSlug] = useState('');
  const [duration, setDuration] = useState<30 | 45 | 60>(60);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [intake, setIntake] = useState<Set<IntakeFieldKey>>(new Set(['fullName', 'email']));
  const [maxDaysOut, setMaxDaysOut] = useState(21);
  const [submitting, setSubmitting] = useState(false);
  const [lastCreatedUrl, setLastCreatedUrl] = useState<string | null>(null);

  const slugPreview = useMemo(() => normalizeSlug(slug), [slug]);

  const toggleIntake = (k: IntakeFieldKey) => {
    setIntake((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slugPreview || slugPreview.length < 2) {
      setError('Slug must be at least 2 characters (a-z, 0-9, hyphen).');
      return;
    }
    if (intake.size === 0) {
      setError('Pick at least one intake field.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const intakeFields = INTAKE_CATALOG
        .filter((c) => intake.has(c.key))
        .map((c) => ({
          id: c.key,
          label: c.label,
          type: c.type,
          required: true,
          placeholder: c.placeholder,
        }));

      const body: CreateBookingScheduleBody = {
        slug: slugPreview,
        durationMinutes: duration,
        maxDaysOut,
        intakeFields,
      };
      if (title.trim()) body.title = title.trim();
      if (subtitle.trim()) body.subtitle = subtitle.trim();

      const created = await createBookingSchedule(body);
      setLastCreatedUrl(created.url);

      // Reset form
      setSlug('');
      setTitle('');
      setSubtitle('');
      setDuration(60);
      setMaxDaysOut(21);
      setIntake(new Set(['fullName', 'email']));

      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (s: BookingScheduleListRow) => {
    if (!confirm(`Disable schedule "${s.slug}"? (Soft delete — set is_active=false. Existing bookings keep working.)`)) return;
    try {
      await deleteBookingSchedule(s.slug, false);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete.');
    }
  };

  const onCopy = (url: string) => {
    navigator.clipboard?.writeText(url).catch(() => { /* ignore */ });
  };

  return (
    <div className="booker">
      <header className="booker-header">
        <h1>Booker</h1>
        <p className="muted">
          Spin up a custom <code>/book/&lt;slug&gt;</code> page for anyone. Clones from
          the <code>zach-hintze</code> template; everything not set below inherits
          (Mon-Fri 9-4 AZ, Adam's calendar, signal-rev.com sender).
        </p>
      </header>

      {error && <div className="booker-error">{error}</div>}

      {/* @block 2.2 Create form */}
      <form className="booker-form" onSubmit={onCreate}>
        <div className="booker-row">
          <label className="booker-field">
            <span>Slug</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="zach-hintze"
              required
            />
            {slugPreview && slugPreview !== slug && (
              <small className="muted">URL slug: <code>{slugPreview}</code></small>
            )}
          </label>

          <label className="booker-field">
            <span>Duration</span>
            <div className="booker-pills">
              {[30, 45, 60].map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`pill ${duration === m ? 'active' : ''}`}
                  onClick={() => setDuration(m as 30 | 45 | 60)}
                >
                  {m} min
                </button>
              ))}
            </div>
          </label>

          <label className="booker-field">
            <span>Window (days out)</span>
            <input
              type="number"
              min={1}
              max={90}
              value={maxDaysOut}
              onChange={(e) => setMaxDaysOut(parseInt(e.target.value, 10) || 21)}
            />
          </label>
        </div>

        <details className="booker-advanced">
          <summary>Page copy (optional)</summary>
          <label className="booker-field">
            <span>Page title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Catch up with Adam (default)"
            />
          </label>
          <label className="booker-field">
            <span>Subtitle</span>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder={`Pick a ${duration === 60 ? '1-hour' : `${duration}-min`} slot that works for you. We will meet on Google Meet.`}
            />
          </label>
        </details>

        <div className="booker-field">
          <span>Intake fields (all required)</span>
          <div className="booker-checks">
            {INTAKE_CATALOG.map((c) => (
              <label key={c.key} className={`booker-check ${intake.has(c.key) ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={intake.has(c.key)}
                  onChange={() => toggleIntake(c.key)}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="booker-actions">
          <button type="submit" disabled={submitting || !slugPreview}>
            {submitting ? 'Creating...' : 'Create booker'}
          </button>
        </div>
      </form>

      {lastCreatedUrl && (
        <div className="booker-success">
          <strong>Live:</strong>{' '}
          <a href={lastCreatedUrl} target="_blank" rel="noopener noreferrer">{lastCreatedUrl}</a>{' '}
          <button type="button" className="copy-btn" onClick={() => onCopy(lastCreatedUrl)}>Copy</button>
          <small className="muted"> (5-min server cache before it goes fully live)</small>
        </div>
      )}

      {/* @block 2.3 List of existing schedules */}
      <div className="booker-list-head">
        <h2>Existing schedules</h2>
        <label className="muted">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />{' '}
          show inactive
        </label>
      </div>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <table className="booker-table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Title</th>
              <th>Duration</th>
              <th>TZ</th>
              <th>Window</th>
              <th>URL</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="muted">No schedules.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.slug} className={r.isActive ? '' : 'dim'}>
                <td><code>{r.slug}</code></td>
                <td>{r.title}</td>
                <td>{r.durationMinutes} min</td>
                <td>{r.timeZoneDisplay}</td>
                <td>{r.maxDaysOut}d</td>
                <td>
                  <a href={r.url} target="_blank" rel="noopener noreferrer">{r.url}</a>{' '}
                  <button type="button" className="copy-btn" onClick={() => onCopy(r.url)}>Copy</button>
                </td>
                <td>{r.isActive ? 'active' : 'inactive'}</td>
                <td>
                  {r.isActive && (
                    <button type="button" className="link danger" onClick={() => onDelete(r)}>Disable</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <style>{`
        .booker { padding: 24px 32px; max-width: 1100px; }
        .booker-header h1 { margin: 0 0 4px; font-size: 22px; }
        .booker-header .muted { margin: 0 0 16px; }
        .muted { color: #94a3b8; }
        code { background: #1e293b; padding: 1px 6px; border-radius: 4px; font-size: 0.9em; }

        .booker-error { background: #4c1f1f; color: #fecaca; padding: 8px 12px; border-radius: 6px; margin-bottom: 12px; }
        .booker-success { background: #143d2e; color: #bbf7d0; padding: 8px 12px; border-radius: 6px; margin: 12px 0; }
        .booker-success a { color: #6ee7b7; }

        .booker-form { background: #111827; border: 1px solid #1e293b; padding: 16px 20px; border-radius: 8px; margin-bottom: 24px; display: flex; flex-direction: column; gap: 12px; }
        .booker-row { display: grid; grid-template-columns: 2fr 1.5fr 1fr; gap: 16px; }
        .booker-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
        .booker-field > span:first-child { font-weight: 500; color: #cbd5e1; }
        .booker-field input[type="text"],
        .booker-field input[type="email"],
        .booker-field input[type="number"] {
          background: #0a0e17; border: 1px solid #1e293b; color: #e2e8f0;
          padding: 8px 10px; border-radius: 4px; font-size: 14px;
        }
        .booker-field input:focus { border-color: #06d6a0; outline: none; }
        .booker-advanced summary { cursor: pointer; color: #94a3b8; padding: 4px 0; }
        .booker-advanced { display: flex; flex-direction: column; gap: 8px; }

        .booker-pills { display: flex; gap: 6px; }
        .pill { background: #0a0e17; border: 1px solid #1e293b; color: #cbd5e1; padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 13px; }
        .pill.active { background: #06d6a0; color: #042f2e; border-color: #06d6a0; font-weight: 600; }

        .booker-checks { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-top: 4px; }
        .booker-check { display: flex; align-items: center; gap: 6px; padding: 6px 8px; border-radius: 4px; cursor: pointer; font-size: 13px; color: #94a3b8; }
        .booker-check.on { background: #143d2e; color: #bbf7d0; }
        .booker-check input { margin: 0; }

        .booker-actions { display: flex; justify-content: flex-end; }
        .booker-actions button {
          background: #06d6a0; color: #042f2e; border: 0; padding: 8px 16px;
          border-radius: 4px; font-weight: 600; cursor: pointer; font-size: 14px;
        }
        .booker-actions button:disabled { background: #1e293b; color: #475569; cursor: not-allowed; }

        .booker-list-head { display: flex; align-items: center; justify-content: space-between; margin: 24px 0 8px; }
        .booker-list-head h2 { margin: 0; font-size: 16px; color: #cbd5e1; }

        .booker-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .booker-table th, .booker-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #1e293b; }
        .booker-table th { color: #94a3b8; font-weight: 500; }
        .booker-table tr.dim td { color: #64748b; }
        .booker-table a { color: #6ee7b7; }
        .booker-table .copy-btn,
        .booker-success .copy-btn {
          background: #1e293b; color: #cbd5e1; border: 0; padding: 2px 8px;
          border-radius: 3px; font-size: 11px; cursor: pointer; margin-left: 4px;
        }
        .booker-table button.link.danger { background: transparent; color: #f87171; border: 0; cursor: pointer; font-size: 12px; padding: 2px 6px; }

        /* Phone (<=767px, matches useIsMobile): own the scroll, stack the form
           row + intake grid, and let the wide 8-col schedules table scroll. */
        @media (max-width: 767px) {
          .booker { padding: 16px 14px; height: 100%; overflow-y: auto; -webkit-overflow-scrolling: touch; }
          .booker-row { grid-template-columns: 1fr; }
          .booker-checks { grid-template-columns: 1fr 1fr; }
          .booker-table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
      `}</style>
    </div>
  );
}

// @section 3 Helpers

function normalizeSlug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}
